import _ from 'lodash';
import BaseModel from './BaseModel';
import moment from 'moment';
import type { Moment } from 'moment';
import { Config } from '../config';
import Change from './Change';
import User from './User';

class Attachment extends BaseModel {
    _id: string;
    name: string;
    type: string;
    size: number;
    change?: AnyObj;
    /** Comment/message that introduced this file (email/Signal intake), when known. */
    comment?: string | null;
    project: string;
    author?: AnyObj;
    createdAt?: Moment;
    updatedAt?: Moment;

    static MIME_TYPES_BMP  = "image/bmp";
    static MIME_TYPES_GIF  = "image/gif";
    static MIME_TYPES_JPG  = "image/jpeg";
    static MIME_TYPES_PNG  = "image/png";
    static MIME_TYPES_WEBP = "image/webp";
    static MIME_TYPES_SVG  = "image/svg+xml";
    static MIME_TYPES_TIF  = "image/tiff";
    static MIME_TYPES_MP4  = "video/mp4";

    constructor(properties: AnyObj) {
        super({})
        this._id       = properties._id;
        this.name      = properties.name;
        this.type      = properties.type;
        this.size      = properties.size;;
        // Populated change objects become Change instances. Bare id strings
        // (Mercure payloads, failed populate) stay as strings so !!change still
        // distinguishes project vs card attachments — but have no .color().
        this.change = properties.change;
        if (_.isPlainObject(this.change)) {
            this.change = new Change(this.change);
        }
        this.comment = properties.comment
            ? (typeof properties.comment === 'string'
                ? properties.comment
                : properties.comment._id || null)
            : null;
        this.project   = properties.project;
        if (properties.author) {
            this.author = new User(properties.author);
        } else {
            this.author = undefined;
        }
        this.createdAt = properties.createdAt ? moment(properties.createdAt) : undefined;
        this.updatedAt = properties.updatedAt ? moment(properties.updatedAt) : undefined;
    }

    /**
     * Public download URL with a cosmetic filename segment for mail/browser UX.
     * The server resolves by `_id` only; the name must never be treated as a disk path.
     */
    downloadUrl() {
        const parts = [
            Config.api_end_point,
            'attachments',
            this._id,
        ];
        const name = String(this.name || '').trim();
        if (name) {
            const base = name.replace(/\\/g, '/').split('/').pop() || name;
            parts.push(encodeURIComponent(base));
        }
        return parts.join('/');
    }

    static createChangeAttachmentUrl(id: string) {
        return [
            Config.api_end_point,
            'changes',
            id,
            'attachments'
        ].join('/');
    }

    static createBugReportAttachmentUrl(id: string) {
        return [
            Config.api_end_point,
            'support',
            id,
            'attachments'
        ].join('/');
    }

    static loadBugReportAttachmentUrl(id: string) {
        return [
            Config.api_end_point,
            'support',
            id,
            'attachments'
        ].join('/');
    }

    static createProjectAttachmentUrl(id: string) {
        return [
            Config.api_end_point,
            'projects',
            id,
            'attachments'
        ].join('/');
    }

    /**
     * True when `change` is a hydrated Change model (callable .color()), not a bare id.
     */
    hasChangeDetails(): boolean {
        return typeof this.change?.color === 'function';
    }

    isPicture() {
        switch(this.type) {
            case Attachment.MIME_TYPES_JPG:
            case Attachment.MIME_TYPES_GIF:
            case Attachment.MIME_TYPES_WEBP:
            case Attachment.MIME_TYPES_PNG:
            case Attachment.MIME_TYPES_TIF:
            case Attachment.MIME_TYPES_SVG:
                return true;
            default :
                return false
        }
    }

    getExtension() {
        return this.name.substr(this.name.lastIndexOf('.') + 1 - this.name.length);
    }

    static acceptedFiles() {
        return [
            '.pdf',
            '.bmp', '.gif', 'jpeg', '.jpg', '.png', '.webp', '.svg', '.psd', '.tiff',
            '.mp4',
            '.odt', '.ods', '.odp', '.odg',
            '.csv', '.xls', '.xlsx', '.doc', '.docx', '.ppt', '.pptx',
            '.css', '.html', '.log', '.md', '.txt', '.xml', '.json', '.yaml', '.yml',
            '.zip', '.bzip', '.bzip2', '.rar', 'tar', '.gz'
        ].join(',');
    }
}

export default Attachment;
